import React, { useContext, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Button, Spinner } from 'reactstrap';
import { actions } from '../../../../../context';
import { DaylightContext } from '../DaylightCodeMapper';

const BulkActionsBar = () => {
    const dispatch = useDispatch();
    const { practiceId, partnerId, canFlagForReview } = useContext(DaylightContext);

    const selectedCodeIds = useSelector(
        (state) => state.daylight.selectedCodeIds,
        shallowEqual
    );

    const daylightCodes = useSelector(
        (state) => state.daylight.daylightCodes,
        shallowEqual
    );

    const isBulkActionInProgress = useSelector(
        (state) => state.daylight.isBulkActionInProgress
    );

    const { allComplete, noneComplete, allNeedsReview, noneNeedsReview } = useMemo(() => {
        const selectedCodes = daylightCodes.filter((c) => selectedCodeIds.includes(c.id));
        return {
            allComplete: selectedCodes.length > 0 && selectedCodes.every((c) => c.confirmationStatus === 1),
            noneComplete: selectedCodes.length > 0 && selectedCodes.every((c) => !c.confirmationStatus || c.confirmationStatus === 0),
            allNeedsReview: selectedCodes.length > 0 && selectedCodes.every((c) => c.needsReview),
            noneNeedsReview: selectedCodes.length > 0 && selectedCodes.every((c) => !c.needsReview),
        };
    }, [daylightCodes, selectedCodeIds]);

    if (selectedCodeIds.length === 0) return null;

    const handleBulkMarkComplete = () => {
        dispatch(actions.daylight.bulkMarkCodesComplete(practiceId, selectedCodeIds, partnerId));
    };

    const handleBulkUnmarkComplete = () => {
        dispatch(actions.daylight.bulkUnmarkCodesComplete(practiceId, selectedCodeIds, partnerId));
    };

    const handleBulkMarkForReview = () => {
        dispatch(actions.daylight.bulkMarkCodesForReview(practiceId, selectedCodeIds, partnerId));
    };

    const handleBulkConfirmMapping = () => {
        dispatch(actions.daylight.bulkUnmarkCodesForReview(practiceId, selectedCodeIds, partnerId));
    };

    const handleBulkDisable = () => {
        dispatch(actions.daylight.bulkDisableCodes(practiceId, selectedCodeIds, partnerId));
    };

    const handleBulkAddMapping = () => {
        dispatch(actions.daylight.setBulkAddMappingModalOpen(true));
    };

    const handleClearSelection = () => {
        dispatch(actions.daylight.clearCodeSelection());
    };

    return (
        <div
            className="d-flex align-items-center justify-content-between px-3 py-2 bg-secondary text-light"
            style={{ borderBottom: '1px solid #444', flexShrink: 0 }}
        >
            <div className="d-flex align-items-center">
                {isBulkActionInProgress && (
                    <Spinner size="sm" className="mr-2" />
                )}
                <span className="font-weight-bold mr-3">
                    {selectedCodeIds.length} code{selectedCodeIds.length > 1 ? 's' : ''} selected
                </span>
            </div>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '4px' }}>
                {canFlagForReview && (
                    <>
                        <Button
                            size="sm"
                            color="success"
                            disabled={isBulkActionInProgress || allComplete}
                            onClick={handleBulkMarkComplete}
                            title={allComplete ? 'All selected codes are already complete' : 'Mark all selected codes as complete'}
                        >
                            Mark Complete
                        </Button>
                        <Button
                            size="sm"
                            color="warning"
                            disabled={isBulkActionInProgress || noneComplete}
                            onClick={handleBulkUnmarkComplete}
                            title={noneComplete ? 'No selected codes are marked complete' : 'Unmark all selected codes from complete'}
                        >
                            Unmark Complete
                        </Button>
                        <Button
                            size="sm"
                            color="info"
                            disabled={isBulkActionInProgress || allNeedsReview}
                            onClick={handleBulkMarkForReview}
                            title={allNeedsReview ? 'All selected codes are already marked for review' : 'Mark all selected codes for review'}
                        >
                            Mark for Review
                        </Button>
                        <Button
                            size="sm"
                            color="primary"
                            disabled={isBulkActionInProgress || noneNeedsReview}
                            onClick={handleBulkConfirmMapping}
                            title={noneNeedsReview ? 'No selected codes are marked for review' : 'Confirm mappings for all selected codes'}
                        >
                            Confirm Mapping
                        </Button>
                        <Button
                            size="sm"
                            color="danger"
                            disabled={isBulkActionInProgress}
                            onClick={handleBulkDisable}
                            title="Disable all selected codes"
                        >
                            Disable
                        </Button>
                        <Button
                            size="sm"
                            color="light"
                            className="text-dark"
                            disabled={isBulkActionInProgress}
                            onClick={handleBulkAddMapping}
                            title="Add a mapping to all selected codes"
                        >
                            Add Mapping
                        </Button>
                    </>
                )}
                <Button
                    size="sm"
                    outline
                    color="light"
                    disabled={isBulkActionInProgress}
                    onClick={handleClearSelection}
                    title="Clear selection"
                >
                    Clear Selection
                </Button>
            </div>
        </div>
    );
};

export default BulkActionsBar;
